import { daysBetween, getBusinessTodayDate } from './dateUtils';

export const HOTBOX_DEFAULT_DATE_RANGE_DAYS = 30;

/**
 * Cleared date-picker value in the Hotbox URL. Distinct from an absent param,
 * which means "use the default last-30-days window".
 */
export const HOTBOX_DATE_DELTA_NONE = 'none' as const;

/**
 * Day offset from the agency's business today, or `none` when the picker is
 * cleared. Negative offsets are in the past.
 */
export type HotboxDateDeltaParam = number | typeof HOTBOX_DATE_DELTA_NONE;

/**
 * Default date range for the Hotbox filter.
 *
 * Matches WellSky behavior: inclusive [today - 30 days, today] using the
 * agency's business calendar so the UI agrees with backend validation.
 */
export function getDefaultHotboxDateRange(): { from: Date; to: Date } {
  const today = getBusinessTodayDate();
  const from = new Date(today);
  from.setDate(from.getDate() - HOTBOX_DEFAULT_DATE_RANGE_DAYS);
  return { from, to: today };
}

/** Convert a calendar date to a day offset from business today. */
export function dateToHotboxDelta(
  date: Date,
  today: Date = getBusinessTodayDate()
): number {
  return daysBetween(today, date);
}

/** Resolve a day offset against business today. */
export function hotboxDeltaToDate(
  delta: number,
  today: Date = getBusinessTodayDate()
): Date {
  const date = new Date(today);
  date.setDate(date.getDate() + delta);
  return date;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return daysBetween(a, b) === 0;
}

/**
 * Resolve URL date deltas into absolute dates for the API / DatePickers.
 *
 * Both params absent → default last-30-days window (recomputed each load so a
 * bookmark stays "recent"). `none` → that bound is cleared.
 */
export function resolveHotboxDateRange(
  fromDelta: HotboxDateDeltaParam | null,
  toDelta: HotboxDateDeltaParam | null
): { from: Date | undefined; to: Date | undefined } {
  if (fromDelta === null && toDelta === null) {
    return getDefaultHotboxDateRange();
  }

  return {
    from:
      fromDelta === null || fromDelta === HOTBOX_DATE_DELTA_NONE
        ? undefined
        : hotboxDeltaToDate(fromDelta),
    to:
      toDelta === null || toDelta === HOTBOX_DATE_DELTA_NONE
        ? undefined
        : hotboxDeltaToDate(toDelta),
  };
}

/**
 * Encode absolute picker dates as day offsets for the URL.
 *
 * The default last-30 window serializes as absent params so a bare `/hotbox`
 * bookmark always means "last 30 days from today", not a fixed calendar span.
 */
export function encodeHotboxDateDeltas(
  from: Date | undefined,
  to: Date | undefined,
  today: Date = getBusinessTodayDate()
): {
  fromDelta: HotboxDateDeltaParam | null;
  toDelta: HotboxDateDeltaParam | null;
} {
  const defaults = getDefaultHotboxDateRange();
  if (
    from !== undefined &&
    to !== undefined &&
    sameCalendarDay(from, defaults.from) &&
    sameCalendarDay(to, defaults.to)
  ) {
    return { fromDelta: null, toDelta: null };
  }

  return {
    fromDelta:
      from === undefined
        ? HOTBOX_DATE_DELTA_NONE
        : dateToHotboxDelta(from, today),
    toDelta:
      to === undefined ? HOTBOX_DATE_DELTA_NONE : dateToHotboxDelta(to, today),
  };
}
